import { searchLocationWithTransaction, searchStaffWithTransaction } from "apis";
import {
  FilterBar,
  FilterDateRange,
  FilterMultiSelect,
  FilterSingleSelect,
  FilterTimeRange,
} from "components/commons";
import { TapStatus, VoucherType } from "enums";
import { useApi, useMount } from "hooks";
import {
  locationWithTransactionFilterResponse,
  staffWithTransactionFilterRequest,
  staffWithTransactionFilterResponse,
  locationMovementFilterRequest,
} from "mappers";
import React, { useCallback, useMemo } from "react";
import { mapObject, mapObjectsToSelect, prettifyTapStatus, prettifyVoucherType } from "services";
import lang from "translations";

const VoucherMovementFilter = ({
  filterState,
  requestState,
  modifyFilter,
  applyFilter,
  clearFilter,
  venueId,
}) => {
  const {
    request: searchLocationRequest,
    loading: loadingLocation,
    mappedData: locations,
  } = useApi({
    api: searchLocationWithTransaction,
    isArray: true,
    mapper: locationWithTransactionFilterResponse,
    handleOwnError: true,
    params: {
      venueId,
    },
  });

  const {
    request: searchStaffRequest,
    loading: loadingStaff,
    mappedData: staffs,
  } = useApi({
    api: searchStaffWithTransaction,
    isArray: true,
    mapper: staffWithTransactionFilterResponse,
    handleOwnError: true,
    params: {
      venueId,
    },
  });

  useMount(() => {
    fetchLocationRequest(requestState);
    fetchStaffRequest(requestState);
  });

  const fetchLocationRequest = useCallback(
    (requestState) => {
      searchLocationRequest(mapObject({ ...requestState }, locationMovementFilterRequest));
    },
    [searchLocationRequest]
  );

  const fetchStaffRequest = useCallback(
    (requestState) => {
      searchStaffRequest(mapObject({ ...requestState }, staffWithTransactionFilterRequest));
    },
    [searchStaffRequest]
  );

  const applyFilterCb = useCallback(
    (searchKey) => {
      return applyFilter(searchKey);
    },
    [applyFilter]
  );

  const clearFilterCb = useCallback(async () => {
    const { requestState } = clearFilter();
    fetchLocationRequest(requestState);
    fetchStaffRequest(requestState);
  }, [clearFilter, fetchLocationRequest, fetchStaffRequest]);

  const changeDateRangeCb = useCallback(
    (name, value) => {
      const { requestState } = modifyFilter(name, { value });
      fetchLocationRequest(requestState);
      fetchStaffRequest(requestState);
    },
    [fetchLocationRequest, fetchStaffRequest, modifyFilter]
  );

  const locationOptions = useMemo(() => {
    return mapObjectsToSelect(locations, { textKey: "name" });
  }, [locations]);

  const staffOptions = useMemo(() => {
    return mapObjectsToSelect(staffs, { textKey: "name" });
  }, [staffs]);

  const voucherTypeOptions = useMemo(() => {
    const voucherTypes = [VoucherType.Issue, VoucherType.Redeem, VoucherType.Remove];
    return voucherTypes.map((type) => {
      return { text: prettifyVoucherType(type), value: type };
    });
  }, []);

  const tapStatusOptions = useMemo(() => {
    const tapStatus = [TapStatus.Success, TapStatus.Failed];
    const prettyTapStatus = tapStatus.map((status) => {
      return { text: prettifyTapStatus(status), value: status };
    });
    return [{ text: lang.allStatus, value: null }, ...prettyTapStatus];
  }, []);

  return (
    <FilterBar
      placeholder={lang.searchMovementPlaceholder}
      className="mb-lg"
      onClear={clearFilterCb}
      onApply={applyFilterCb}
      filterState={filterState}
    >
      <FilterDateRange
        id="dateRange"
        label={lang.dates}
        value={filterState.dateRange}
        onChange={(value) => changeDateRangeCb("dateRange", value)}
      />
      <FilterTimeRange
        id="timeRange"
        label={lang.time}
        value={filterState.dateRange}
        onChange={(value) => modifyFilter("dateRange", { value })}
      />
      <FilterMultiSelect
        id="locations"
        label={lang.outlet}
        allLabel={lang.allLocations}
        emptyLabel={lang.noLocationAvailable}
        disableWhenEmpty={false}
        loading={loadingLocation}
        options={locationOptions}
        value={filterState.locations}
        onChange={(value) => modifyFilter("locations", { value })}
      />
      <FilterMultiSelect
        id="staffs"
        label={lang.staff}
        allLabel={lang.allStaff}
        emptyLabel={lang.noAvailableStaff}
        disableWhenEmpty={false}
        loading={loadingStaff}
        options={staffOptions}
        value={filterState.staffs}
        onChange={(value) => modifyFilter("staffs", { value })}
      />
      <FilterMultiSelect
        id="voucherTypes"
        label={lang.voucherType}
        allLabel={lang.allVoucherTypes}
        options={voucherTypeOptions}
        value={filterState.voucherTypes}
        onChange={(value) => modifyFilter("voucherTypes", { value })}
      />
      <FilterSingleSelect
        id="tapStatus"
        label={lang.tapStatus}
        summaryFallback={lang.allStatus}
        options={tapStatusOptions}
        value={filterState.tapStatus}
        onChange={(value) => modifyFilter("tapStatus", { value })}
      />
    </FilterBar>
  );
};

export default VoucherMovementFilter;
